import React from "react";

import type {
  Team,
  Teams as TeamsReport,
} from "apps/company/api/rest/company-dashboard-engagement";
import { useTranslation } from "lib/i18n/use-translation";

import { Card, NotEnoughData } from "../engagement/EngagementTab";
import { LimitedDataset } from "../resilience/parts";
import styles from "./engagement-redesign.module.scss";

const K = "admin:companyDashboard.engagement";

// Safari drops a list's role once its markers are styled away.
const LIST = "list";

// The prototype's bars, highest rate first; any team past the eighth takes
// the last.
const COLOURS = [
  "#ff875e",
  "#e07848",
  "#c3bfba",
  "#ab9895",
  "#6c625c",
  "#444444",
  "#333333",
  "#212121",
];

const Row = ({
  name,
  rate,
  width,
  colour,
}: {
  name: string;
  rate: string | null;
  width: number;
  colour: string;
}) => (
  <li className={styles["team"]}>
    <span className={styles["team-head"]}>
      <span className={styles["team-name"]}>{name}</span>
      {rate !== null && <span className={styles["team-rate"]}>{rate}</span>}
    </span>
    {rate === null ? (
      <NotEnoughData />
    ) : (
      <span className={styles["team-track"]} aria-hidden="true">
        <span
          data-testid="team-bar"
          style={{ width: `${width * 100}%`, backgroundColor: colour }}
        />
      </span>
    )}
  </li>
);

// Activation for the whole company, whatever group the rest of the page is
// for: each department's rate in the order Platform ranks them, the longest
// bar the highest, as in the prototype. With no department to show, the
// company's own rate under the Resilience page's limited-dataset note.
const Teams = ({
  teams,
  day,
  filtered,
}: {
  teams: TeamsReport;
  day: string;
  filtered: boolean;
}) => {
  const { t, i18n } = useTranslation(["admin"]);
  const percent = new Intl.NumberFormat(i18n.language, {
    style: "percent",
    maximumFractionDigits: 0,
  });
  // Rounding never turns some activation into 0% or most into 100%.
  const format = (share: number) => {
    if (share > 0 && share < 0.005) {
      return t(`${K}.teams.below`, { rate: percent.format(0.01) });
    }
    if (share < 1 && share >= 0.995) {
      return t(`${K}.teams.above`, { rate: percent.format(0.99) });
    }
    return percent.format(share);
  };
  const share = (team: Team) => team.activated / team.employees_in_scope;
  const departments = teams.departments;
  const highest = Math.max(0, ...departments.map(share));

  // A share of a withheld count would reveal it, so both must be shown.
  const { employees_in_scope: inScope, activated } = teams.company;
  const companyShare =
    inScope.suppressed || activated.suppressed || inScope.value === 0
      ? null
      : activated.value / inScope.value;

  return (
    <Card
      title={t(`${K}.teams.title`)}
      description={t(
        filtered ? `${K}.teams.wholeCompany` : `${K}.teams.description`
      )}
    >
      {departments.length === 0 && (
        <div className={styles["teams-limited"]}>
          <LimitedDataset
            section={t(`${K}.teams.breakdown`)}
            message={t(`${K}.teams.limited`)}
            body={t(`${K}.teams.limitedBody`)}
          />
        </div>
      )}
      <ul className={styles["teams"]} role={LIST}>
        {departments.length === 0 ? (
          <Row
            name={t(`${K}.teams.companyWide`)}
            rate={companyShare === null ? null : format(companyShare)}
            width={companyShare ?? 0}
            colour={COLOURS[0]}
          />
        ) : (
          departments.map((team, index) => (
            <Row
              key={team.name}
              name={team.name}
              rate={format(share(team))}
              width={highest === 0 ? 0 : share(team) / highest}
              colour={COLOURS[Math.min(index, COLOURS.length - 1)]}
            />
          ))
        )}
      </ul>
      <p className={styles["team-note"]}>
        {t(`${K}.teams.dataThrough`, { date: day })}
      </p>
    </Card>
  );
};

export default Teams;
